import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  AddressDto,
  AppendAddressRequest,
  CustomerCompanyProfile,
  CustomerHierarchy,
} from '../types/salesOrder.types';

interface AppendAddressResponse {
  internalId: string;
}

class CustomerAddressService {
  private readonly baseUrl = '/sales-orders/customers';

  async list(customerId: string, signal?: AbortSignal): Promise<AddressDto[]> {
    const { data } = await apiClient.get<ApiResponse<AddressDto[]>>(
      `${this.baseUrl}/${customerId}/addresses`,
      { signal },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load customer addresses');
  }

  async append(customerId: string, request: AppendAddressRequest): Promise<string> {
    const { data } = await apiClient.post<ApiResponse<AppendAddressResponse>>(
      `${this.baseUrl}/${customerId}/addresses`,
      request,
    );
    if (data.success && data.data) return data.data.internalId;
    throw new Error(data.message || 'Failed to add address to customer');
  }

  // FO-SOI-3: Deliver/Bill-to company hierarchy (parent + project children).
  async hierarchy(customerId: string, signal?: AbortSignal): Promise<CustomerHierarchy> {
    const { data } = await apiClient.get<ApiResponse<CustomerHierarchy>>(
      `${this.baseUrl}/${customerId}/hierarchy`,
      { signal },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load customer hierarchy');
  }

  // FO-SOI-3: read-only customer-derived profile (terms, tax num, document alerts).
  async profile(customerId: string, signal?: AbortSignal): Promise<CustomerCompanyProfile> {
    const { data } = await apiClient.get<ApiResponse<CustomerCompanyProfile>>(
      `${this.baseUrl}/${customerId}/profile`,
      { signal },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load customer profile');
  }
}

export const customerAddressService = new CustomerAddressService();
